import { FC, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Input,
  RangeSlider,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  RangeSliderTrack,
  Select,
} from '@chakra-ui/react';
import { usePropChange } from '@netiva/classifieds-common';

import { useAppDispatch, useAppSelector } from '@/store';
import { adActions } from '@/store/ad';
import { AdAttribute } from '@/store/ad/types';

export type RangeIntegerInputProps = {
  attribute: AdAttribute;
  allowedValues?: number[];
};

const parseStoredRange = (value: string | undefined, fallbackMin: number, fallbackMax: number): [number, number] => {
  if (!value) return [fallbackMin, fallbackMax];
  const parsed = JSON.parse(value) as { min?: string | number; max?: string | number };
  const min = Number(parsed.min);
  const max = Number(parsed.max);
  return [Number.isNaN(min) ? fallbackMin : min, Number.isNaN(max) ? fallbackMax : max];
};

const valueToIndex = (value: number, allowed: number[]) => {
  const idx = allowed.indexOf(value);
  return idx === -1 ? 0 : idx;
};

export const RangeIntegerInput: FC<RangeIntegerInputProps> = ({ attribute, allowedValues }) => {
  const dispatch = useAppDispatch();
  const { formData } = useAppSelector((state) => state.ad);

  const minBound = allowedValues ? allowedValues[0] : (attribute.minValue ?? 0);
  const maxBound = allowedValues ? allowedValues[allowedValues.length - 1] : (attribute.maxValue ?? 100);

  const storedValue = formData[attribute.id]?.value;
  const initialRange = useMemo(
    () => parseStoredRange(storedValue, minBound, maxBound),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [storedValue]
  );

  const toSlider = (min: number, max: number): [number, number] =>
    allowedValues ? [valueToIndex(min, allowedValues), valueToIndex(max, allowedValues)] : [min, max];

  const fromSlider = (minIdx: number, maxIdx: number): [number, number] =>
    allowedValues ? [allowedValues[minIdx], allowedValues[maxIdx]] : [minIdx, maxIdx];

  const [rangeValue, setRangeValue] = useState<[number, number]>(() => toSlider(...initialRange));
  const [inputValue, setInputValue] = useState<[string, string]>([String(initialRange[0]), String(initialRange[1])]);

  if (usePropChange(initialRange)) {
    setRangeValue(toSlider(...initialRange));
    setInputValue([String(initialRange[0]), String(initialRange[1])]);
  }

  const [currentMin, currentMax] = fromSlider(...rangeValue);

  const commit = (minIdx: number, maxIdx: number) => {
    const [min, max] = fromSlider(minIdx, maxIdx);
    dispatch(adActions.updateFormData({ key: attribute.id, value: `{"min":"${min}","max":"${max}"}` }));
  };

  const handleSliderChange = ([min, max]: [number, number]) => {
    setRangeValue([min, max]);
    const [actualMin, actualMax] = fromSlider(min, max);
    setInputValue([String(actualMin), String(actualMax)]);
  };

  const handleSelectMin = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newMin = valueToIndex(parseInt(e.target.value), allowedValues!);
    const newMax = Math.max(newMin, rangeValue[1]);
    setRangeValue([newMin, newMax]);
    commit(newMin, newMax);
  };

  const handleSelectMax = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newMax = valueToIndex(parseInt(e.target.value), allowedValues!);
    const newMin = Math.min(rangeValue[0], newMax);
    setRangeValue([newMin, newMax]);
    commit(newMin, newMax);
  };

  const commitInputs = () => {
    const parsedMin = parseInt(inputValue[0]);
    const parsedMax = parseInt(inputValue[1]);
    const safeMin = isNaN(parsedMin) ? minBound : parsedMin;
    const safeMax = isNaN(parsedMax) ? maxBound : parsedMax;
    const validMin = Math.max(minBound, Math.min(safeMin, safeMax));
    const validMax = Math.min(maxBound, Math.max(safeMin, safeMax));
    setRangeValue([validMin, validMax]);
    setInputValue([String(validMin), String(validMax)]);
    commit(validMin, validMax);
  };

  const sliderMin = allowedValues ? 0 : minBound;
  const sliderMax = allowedValues ? allowedValues.length - 1 : maxBound;

  return (
    <Box pl={2} pr={2}>
      <Flex direction="column" gap={2}>
        <Flex justify="space-between" align="center" gap={2}>
          <RangeSlider
            min={sliderMin}
            max={sliderMax}
            step={1}
            value={rangeValue}
            onChange={handleSliderChange}
            onChangeEnd={([min, max]) => commit(min, max)}
            flex={1}
          >
            <RangeSliderTrack>
              <RangeSliderFilledTrack />
            </RangeSliderTrack>
            <RangeSliderThumb index={0} />
            <RangeSliderThumb index={1} />
          </RangeSlider>
        </Flex>

        <Flex justify="space-between">
          {allowedValues ? (
            <Select size="sm" width="auto" value={currentMin} onChange={handleSelectMin}>
              {allowedValues.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </Select>
          ) : (
            <Input
              type="number"
              size="xs"
              width="50px"
              textAlign="center"
              value={inputValue[0]}
              onChange={(e) => setInputValue([e.target.value, inputValue[1]])}
              onBlur={commitInputs}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur();
              }}
            />
          )}
          {allowedValues ? (
            <Select size="sm" width="auto" value={currentMax} onChange={handleSelectMax}>
              {allowedValues.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </Select>
          ) : (
            <Input
              type="number"
              size="xs"
              width="50px"
              textAlign="center"
              value={inputValue[1]}
              onChange={(e) => setInputValue([inputValue[0], e.target.value])}
              onBlur={commitInputs}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.currentTarget.blur();
              }}
            />
          )}
        </Flex>
      </Flex>
    </Box>
  );
};
